import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import { hideCardForTime } from '@elem/ui-common/utils/hideCardForTime';
import classNames from 'classnames';
import editorEventsBus from 'lowcode/utils/editorEventsBus';
import React from 'react';
import EfActivityScore from '../ef-activity-score';
import EfImg from '../ef-img';
import EfLink from '../ef-link';
import BuyLink from './BuyLink';
import BuyLinkNoLogin from './BuyLinkNoLogin';
import './index.scss';
import { langText } from './langText';
import getLocal from '@/app/utils/getLocal';

interface dataLayerType {
    event?: string;
    event_name?: 'buy' | string;
    product_name?: string;
    position?: string;
    [key: string]: any;
}
interface EfBundlesActivityTitleItemProps {
    /**
     * 选中的key
     */
    titleKey: string;
    /**
     * title文案
     */
    title: string;
}
export interface EfBundlesActivityProps {
    /**
     * 主图
     */
    img: string;
    /**
     * 产品 productId
     */
    productId: string;
    /**
     * 产品 variantId
     */
    variantId: string;
    /**
     * 产品实际价格
     */
    discountPrice: number;
    /**
     * 产品划线价格
     */
    originalPrice: number;
    /**
     * 默认购买数量
     */
    quantity: number;
    /**
     * 库存
     */
    inventoryQuantity?: number;
    /**
     * 产品开始展示时间
     */
    timeStart?: string;
    /**
     * 产品结束展示时间
     */
    timeEnd?: string;
    /**
     * 是否点击后添加到购物车
     */
    buyInCart?: boolean;
    /**
     * 文案-主标题
     */
    name?: string;
    /**
     * 文案-主标题--列表
     */
    titleList?: EfBundlesActivityTitleItemProps[];
    /**
     * 文案-主标题--选中的key
     */
    titleKey?: string;
    /**
     * tab 切换回调
     */
    titleChange?: (titleItem: EfBundlesActivityTitleItemProps) => void;
    /**
     * 文案-特性
     */
    power?: string;
    /**
     * 文案-描述
     */
    desc?: string;
    /**
     * 是否需要登录
     */
    isNeedLogin?: boolean;
    /**
     * 是否已登录
     */
    isLogin?: boolean;
    /**
     * 更多详情链接
     */
    moreLink?: string;
    /**
     * 购买按钮状态
     */
    buyStatus?: 'default' | 'disabled';
    /**
     * buy按钮文案
     */
    buyText?: string;
    /**
     * 登录按钮文案
     */
    loginText?: string;
    /**
     * 用于埋点的list_name
     */
    list_name?: string;
    /**
     * 样式名
     */
    className?: string;
    /**
     * 埋点信息
     */
    ldataLayer?: dataLayerType;
    /**
     * 按钮文案
     */
    btnText?: {
        price1_front: string;
        price2_front: string;
        link_more: string;
        link_buy: string;
        sold_out: string;
    };
}

/**
 * 维护:谢保林
 *
 * 用于 Bundles 的商品卡片
 */
const EfBundlesActivity: React.FC<EfBundlesActivityProps> = ({
    img,
    productId,
    variantId,
    discountPrice,
    originalPrice,
    quantity,
    inventoryQuantity,
    timeStart,
    timeEnd,
    buyInCart,
    isNeedLogin,
    isLogin,
    name,
    titleList,
    titleKey,
    titleChange,
    power,
    desc,
    moreLink,
    buyText,
    loginText,
    buyStatus,
    list_name,
    className,
    ldataLayer,
    btnText,
}) => {
    const item = {
        img,
        productId,
        variantId,
        discountPrice,
        originalPrice,
        quantity,
        inventoryQuantity,
        timeStart,
        timeEnd,
        buyInCart,
        name,
        power,
        desc,
        moreLink,
        buyText,
        loginText,
        ldataLayer,
    };

    if (!variantId) {
        return null;
    }
    // 如果有传入(有效时间)
    if (hideCardForTime(item.timeStart, item.timeEnd)) {
        return null;
    }
    const { locale = 'us' } = getLocal();
    const textA = btnText || langText[locale] || langText['us']; //卡片内非传入文本
    const prefixCls = `ef-bundles-activity`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-buy-${buyStatus}`]: buyStatus,
    });
    const isTitleList = titleList?.length;

    // 点击购买--打开购物车
    const buyInCartHandle = () => {
        editorEventsBus.emit(
            'efshopingcart-add',
            [
                {
                    item: item,
                    quantity: 1,
                },
            ],
            (list: any) => {
                //console.log('list', list);
            },
        );
    };

    const showLoginBuyBtn = isNeedLogin && !isLogin; //需要登录,没有登录
    const discount = Math.round((1 - discountPrice / originalPrice) * 100) || 0;
    const score = Math.round((discountPrice / 150) * 5) || 0;

    return (
        <div className={classes}>
            <div className={`${prefixCls}-discount`}>
                {discount > 0 && (
                    <div className={`${prefixCls}-off`}>
                        <span className={`${prefixCls}-off-text`}>
                            <span className={`${prefixCls}-off-text1`}>{`${discount}%`}</span>
                            <span className={`${prefixCls}-off-text2`}>OFF</span>
                        </span>
                    </div>
                )}
                <EfActivityScore className={`${prefixCls}-score`} scaleText="5倍" scoreText={`${score.toLocaleString(locale)}ポイント`} />
            </div>
            <div className={`${prefixCls}-img`}>
                <EfImg pc={item.img} />
            </div>

            {item.name && !isTitleList && <p className={`${prefixCls}-title`}>{item.name}</p>}
            {isTitleList && (
                <div className={`${prefixCls}-title-tab`}>
                    {titleList?.map((titleItem: EfBundlesActivityTitleItemProps) => (
                        <div
                            className={`${prefixCls}-title-tab-item${titleKey === titleItem.titleKey ? ` ${prefixCls}-title-tab-item-active` : ''}`}
                            key={titleItem.titleKey}
                            onClick={() => titleChange?.(titleItem)}
                        >
                            {titleItem.title}
                        </div>
                    ))}
                </div>
            )}
            <div className={`${prefixCls}-footer`}>
                <div className={`${prefixCls}-total`}>
                    <div className={`${prefixCls}-total-discount`}>
                        {textA.price1_front}
                        <span className={`${prefixCls}-weight`}>
                            {FormatMoneyLocal(item.discountPrice || 0, locale)}
                            {locale == 'jp' && <span className="suffix">（税込）</span>}
                        </span>
                    </div>
                    {item?.originalPrice && (
                        <div className={`${prefixCls}-total-original`}>
                            {textA.price2_front}
                            {FormatMoneyLocal(item.originalPrice || 0, locale)}
                            {locale == 'jp' && <span className="suffix">（税込）</span>}
                        </div>
                    )}
                </div>
                <div className={`${prefixCls}-links`}>
                    {item.moreLink && (
                        <EfLink
                            className={`${prefixCls}-link ${prefixCls}-link-more`}
                            href={item.moreLink}
                            dataLayer={
                                item.ldataLayer || {
                                    event: 'uaEvent',
                                    event_name: 'select_product',
                                    list_name: list_name || '',
                                    product_name: item.name || '',
                                    button_name: textA.link_more,
                                }
                            }
                        >
                            {textA.link_more}
                        </EfLink>
                    )}
                    {showLoginBuyBtn && <BuyLinkNoLogin prefixCls={prefixCls} locale={locale} textA={textA} item={item} />}
                    {!showLoginBuyBtn && <BuyLink locale={locale} prefixCls={prefixCls} textA={textA} item={item} buyInCartHandle={buyInCartHandle} />}
                </div>
            </div>
        </div>
    );
};

export default EfBundlesActivity;
